'use client';

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Skeleton,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
} from '@mui/icons-material';
import { IInvoiceViewModel } from 'viewModels';
import { InvoiceStatus } from 'types/enums';
import InvoiceController from 'controllers/InvoiceController';
import { PaymentsController } from 'controllers/PaymentsController';
import { formatCurrency, formatDate } from 'utils';
import InvoiceDetailsModal from '../Modal/InvoiceDetailsModal';

interface InvoiceListProps {
  userId: string;
  isAdmin?: boolean;
}

type ViewMode = 'grid' | 'list';

const InvoiceList: React.FC<InvoiceListProps> = ({
  userId,
  isAdmin = false,
}) => {
  const [invoices, setInvoices] = useState<IInvoiceViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  const loadInvoices = async (pageNumber: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const response = await InvoiceController.getUserInvoices(userId, {
        page: pageNumber,
        take: 10,
      });

      if (response.success) {
        if (pageNumber === 1) {
          setInvoices(response.data);
        } else {
          setInvoices(prev => [...prev, ...response.data]);
        }
        setHasMore(response.data.length === 10);
      } else {
        setError('Erro ao carregar faturas');
      }
    } catch (err) {
      setError('Erro ao carregar faturas');
      console.error('Erro ao carregar faturas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
  }, [userId]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadInvoices(nextPage);
  };

  const handleExportPdf = async (invoiceId: string) => {
    try {
      // Buscar a fatura para obter os dados necessários
      const invoice = invoices.find(inv => inv.id === invoiceId);
      if (!invoice) return;

      // Converter datas para string no formato YYYY-MM-DD
      const startDateStr = new Date(invoice.startDate)
        .toISOString()
        .split('T')[0];
      const endDateStr = new Date(invoice.endDate).toISOString().split('T')[0];

      await InvoiceController.exportInvoicePdf(invoiceId);
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
    }
  };

  const handleMarkAsPaid = async (invoiceId: string) => {
    // Confirmação antes de marcar como paga
    const confirmed = window.confirm(
      'Tem certeza que deseja marcar esta fatura como paga? Esta ação não pode ser desfeita.'
    );

    if (!confirmed) return;

    try {
      const response = await InvoiceController.markInvoiceAsPaid(invoiceId);
      if (response.success) {
        // Atualiza a lista de faturas
        setInvoices(prev =>
          prev.map(invoice =>
            invoice.id === invoiceId
              ? { ...invoice, status: InvoiceStatus.PAID }
              : invoice
          )
        );
      }
    } catch (err) {
      console.error('Erro ao marcar como paga:', err);
    }
  };

  const handleViewDetails = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.OPEN:
        return 'warning';
      case InvoiceStatus.CLOSED:
        return 'info';
      case InvoiceStatus.PAID:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: InvoiceStatus) => {
    switch (status) {
      case InvoiceStatus.OPEN:
        return 'Aberta';
      case InvoiceStatus.CLOSED:
        return 'Fechada';
      case InvoiceStatus.PAID:
        return 'Paga';
      default:
        return 'Desconhecido';
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Faturas
        </Typography>
        <Grid container spacing={2}>
          {[1, 2, 3].map(item => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Faturas</Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(event, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
            }
          }}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewListIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {invoices.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Nenhuma fatura encontrada
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {invoices.map(invoice => (
                <Grid item xs={12} md={6} lg={4} key={invoice.id}>
                  <Card>
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={2}
                      >
                        <Typography variant="h6" component="div">
                          {invoice.invoiceNumber ||
                            `Fatura #${invoice.id.slice(0, 8)}`}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusText(invoice.status)}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                          />
                          {invoice.status === InvoiceStatus.CLOSED &&
                            !isAdmin && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: '0.7rem' }}
                              >
                                (Aguardando pagamento)
                              </Typography>
                            )}
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Período: {formatDate(invoice.startDate)} a{' '}
                        {formatDate(invoice.endDate)}
                      </Typography>

                      <Typography
                        variant="h5"
                        component="div"
                        color="primary"
                        gutterBottom
                      >
                        {formatCurrency(invoice.totalAmount)}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {invoice.transactions.length} transações
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Fechada em:{' '}
                        {invoice.closedAt
                          ? formatDate(invoice.closedAt)
                          : 'N/A'}
                      </Typography>

                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mt={2}
                      >
                        <Box display="flex" gap={1}>
                          <Tooltip title="Visualizar detalhes">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(invoice.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Baixar PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleExportPdf(invoice.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        {/* Botão de marcar como paga - apenas para admins */}
                        {invoice.status === InvoiceStatus.CLOSED &&
                          (isAdmin ? (
                            <Tooltip title="Marcar como paga">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleMarkAsPaid(invoice.id)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Apenas administradores podem marcar faturas como pagas">
                              <Box>
                                <IconButton
                                  size="small"
                                  color="default"
                                  disabled
                                  sx={{ opacity: 0.5 }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fatura</TableCell>
                    <TableCell>Período</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="center">Transações</TableCell>
                    <TableCell>Fechada em</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(invoice => (
                    <TableRow
                      key={invoice.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f8fafc',
                        },
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ReceiptIcon color="primary" fontSize="small" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {invoice.invoiceNumber ||
                                `Fatura #${invoice.id.slice(0, 8)}`}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {invoice.userName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(invoice.startDate)} a{' '}
                          {formatDate(invoice.endDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusText(invoice.status)}
                            color={getStatusColor(invoice.status) as any}
                            size="small"
                          />
                          {invoice.status === InvoiceStatus.CLOSED &&
                            !isAdmin && (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                sx={{ fontStyle: 'italic' }}
                              >
                                Aguardando pagamento
                              </Typography>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {formatCurrency(invoice.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {invoice.transactions.length}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {invoice.closedAt
                            ? formatDate(invoice.closedAt)
                            : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="Visualizar detalhes">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(invoice.id)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Baixar PDF">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleExportPdf(invoice.id)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>

                          {/* Botão de marcar como paga - apenas para admins */}
                          {invoice.status === InvoiceStatus.CLOSED &&
                            (isAdmin ? (
                              <Tooltip title="Marcar como paga">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleMarkAsPaid(invoice.id)}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Apenas administradores podem marcar faturas como pagas">
                                <IconButton
                                  size="small"
                                  color="default"
                                  disabled
                                  sx={{ opacity: 0.5 }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {hasMore && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Carregando...' : 'Carregar mais'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Modal de Detalhes */}
      {showDetailsModal && selectedInvoiceId && (
        <InvoiceDetailsModal
          open={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedInvoiceId(null);
          }}
          invoiceId={selectedInvoiceId}
          isAdmin={isAdmin}
        />
      )}
    </Box>
  );
};

export default InvoiceList;
